// frontend/src/pages/PurchaseOrders.jsx

import { useState, useCallback, useMemo, useRef } from "react";
import TabLink from '../tabs/TabLink';
import { useOpenPurchaseOrder, useClosePurchaseOrderTab } from '../hooks/useOpenPurchaseOrder';
import { useTranslation } from 'react-i18next';
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  Box,
  Chip,
  Typography,
} from "@mui/material";
import PageBanner from "../components/common/PageBanner";
import { PoStatusCell } from '../components/grid/StatusChipCells';
import { poShowsReceipts } from '../components/purchase-orders/poReceiptColumns';
import { canReschedule } from '../components/purchase-orders/poActions';
import Tile from '../components/layout/Tile';
import { ItemFilterBar, useItemFilter } from '../features/item-filter';
import TypeaheadSearchField from '../components/common/TypeaheadSearchField';
import { useTypeaheadSuggestions } from '../hooks/useTypeaheadSuggestions';
import { useTabPersistedState } from '../tabs/useTabPersistedState';
import { useReceiptHistory } from '../hooks/usePOData';
import { useReschedulePO } from '../hooks/usePOMutations';
import { useStoreToday } from '../hooks/useStoreToday';
import { useWriteGate } from '../hooks/useWriteGate';
import { usePurchaseOrderLifecycle } from '../components/purchase-orders/usePurchaseOrderLifecycle';
import { PurchaseOrderRowActions, PurchaseOrderRowActionsScope } from '../components/purchase-orders/PurchaseOrderActions';
import { translateRequestError } from '../queries/api';
import { useAgGridTheme } from '../utils/agGridTheme';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, actionColumn, flexColumn, everyRowIsMaster } from '../utils/gridDefaults';
import GridExportButton from '../components/common/GridExportButton';
import FilterChips from '../components/common/FilterChips';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useSsrmGrid, buildSsrmUrl, buildExportQuery, appendUserParams } from '../hooks/useSsrmGrid';
import { useChangeEffect } from '../hooks/useChangeEffect';

import { useFormatters } from '../hooks/useFormatters';
import { useActiveErp } from '../hooks/useConnectors';
import { buildPinnedTotalsRow, isPinnedTotalRow, wrapColumnsForSummaryRows } from '../utils/pinnedTotals';
import DetailGrid from '../components/grid/DetailGrid';
import ExpandAllButton from '../components/grid/ExpandAllButton';
import { useExpandAllRows } from '../hooks/useExpandAllRows';
import { useIsShopify, itemIdentityColumns } from '../components/grid/productIdentity';
import '../styles/pages.css';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// The status buttons, each with the derived PO statuses it shows
// (PurchaseOrderHeader.PO_STATUS_VALUES), sent to the list as a CSV.
const STATUS_FILTERS = {
  active: ['open', 'partially_received'],
  draft: ['draft'],
  completed: ['received', 'closed', 'cancelled'],
};
const statusParam = (statusFilter) => STATUS_FILTERS[statusFilter].join(',');

function PODetailRenderer({ data }) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const { formatCurrency, formatDate } = useFormatters();
  // PO lines are denominated in the supplier's currency (PurchaseOrderLine.Cost
  // is supplier ccy per the model). The PO header always carries it
  // (PurchaseOrderHeader.CurrencyCode is non-null), so the suffix and per-row
  // formatting derive from there — never the store currency, which would
  // mislabel cross-currency POs.
  const poCurrency = data.currency.currency_code;
  const poCurrencySuffix = ` (${poCurrency})`;

  // TanStack-cached per PO. Expanding multiple PO detail rows in a session
  // shares the cache; collapsing+re-expanding the same PO is instant.
  const { data: receipts, error: receiptsError } = useReceiptHistory(data.purchase_order_header_id);

  const showsReceipts = poShowsReceipts(data.status);

  const detailColumnDefs = useMemo(() => {
    const cols = [
      ...itemIdentityColumns(isShopify, {
        productHeader: t('purchaseOrders.detail.product'),
        codeHeader: t('purchaseOrders.detail.itemCode'),
        skuHeader: t('purchaseOrders.detail.sku'),
        nameHeader: t('purchaseOrders.detail.item'),
      }),
      {
        field: "Quantity",
        headerName: t('purchaseOrders.detail.ordered'),
        type: 'numberColumn',
      },
      ...(showsReceipts ? [
        {
          field: "QuantityReceived",
          headerName: t('purchaseOrders.detail.received'),
          type: 'numberColumn',
        },
        {
          headerName: t('purchaseOrders.detail.outstanding'),
          type: 'numberColumn',
          valueGetter: (p) => p.data.Quantity - p.data.QuantityReceived,
        },
      ] : []),
      // Shown exactly when the PO's PDF prints prices (`shows_prices`, the
      // server's rule) — a draft hides them until the store opts in.
      ...(data.shows_prices ? [{
        field: "Cost",
        headerName: t('purchaseOrders.detail.unitCost') + poCurrencySuffix,
        type: 'decimalColumn',
        valueFormatter: (p) => formatCurrency(p.value, poCurrency),
      }] : []),
      {
        field: "line_status",
        headerName: t('purchaseOrders.detail.status'),
        type: 'centeredColumn',
        cellRenderer: PoStatusCell,
      },
    ];

    return cols;
  }, [t, isShopify, formatCurrency, poCurrency, poCurrencySuffix, showsReceipts, data.shows_prices]);

  const receiptNotes = useMemo(() => {
    if (!receipts) return [];
    return receipts.filter(r => r.Notes);
  }, [receipts]);

  return (
    <DetailGrid
      rowData={data.order_lines}
      columnDefs={detailColumnDefs}
      stateKey="purchase_orders_detail"
      // The lines come from the master row; the receipts feed only this
      // footer. A failed receipts fetch must read as a failure, not as "no
      // notes" — surface it where the notes live.
      footer={receiptsError ? (
        <Box sx={{ mt: 1, fontSize: 12, color: 'error.main' }}>
          {t('purchaseOrders.detail.receiptHistoryError')}
        </Box>
      ) : receiptNotes.length > 0 ? (
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'var(--color-surface)', borderRadius: 1, fontSize: 12 }}>
          <Box sx={{ fontWeight: 'var(--font-weight-semibold)', mb: 0.5 }}>
            {t('purchaseOrders.detail.deliveryNotes')}
          </Box>
          {receiptNotes.map(r => (
            <Box key={r.GoodsReceiptID} sx={{ color: 'text.secondary' }}>
              <strong>{r.ReceiptNumber}</strong> ({formatDate(r.ReceiptDate)})
              {r.created_by_name && ` — ${r.created_by_name}`}
              : {r.Notes}
            </Box>
          ))}
        </Box>
      ) : null}
    />
  );
}

// Dedicated PO-number filter, mounted in the filter row's `leading` slot —
// first box on the row, ahead of the item search. It is a plain identifier
// box, not a second item search: the merchant arrives with a number off a
// printed order, a supplier email or the ERP, and that number, not an item,
// is what this page is looked up by.
//
// It is the SAME widget as the item box beside it (TypeaheadSearchField), so
// the row has one way to search and no box behaves specially: typing offers
// the matching purchase orders, picking one filters to it, Enter applies the
// whole term as a substring match (po_viewset: PONumber__icontains), and
// emptying the box or clicking the X drops the applied term on the spot. That
// last rule is not decoration — an applied filter sitting behind an empty box
// is the invisible-filter bug `useItemFilter.onInputChange` exists to prevent.
//
// `scope` is the grid's own filter set (`ssrmParams`). The suggestions are
// fetched under it, so what the dropdown offers is what the grid will show —
// see po_viewset.suggest. Its `po_number` is the COMMITTED term and is
// replaced here by the draft: the dropdown answers what is being typed.
//
// The term is trimmed once, here, so the grid query, the export and the
// no-rows overlay cannot disagree about what is applied.
const PO_SUGGEST_ENDPOINT = '/api/po-calculations/purchase-orders/suggest/';

function PoNumberFilter({ value, onCommit, scope }) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const [draft, setDraft] = useState(value);

  // Follow the committed term when it moves under us — a picked suggestion
  // commits through the page. A box still showing the old number would be a
  // filter the merchant can read but the grid isn't applying. React's
  // documented shape for this: compare against the previous prop in render,
  // not in an effect.
  const [lastCommitted, setLastCommitted] = useState(value);
  if (value !== lastCommitted) {
    setLastCommitted(value);
    setDraft(value);
  }

  const buildUrl = useCallback((typed) => {
    const search = new URLSearchParams();
    appendUserParams(search, { ...scope, po_number: typed });
    return `${PO_SUGGEST_ENDPOINT}?${search.toString()}`;
  }, [scope]);

  const { suggestions, loading, failed } = useTypeaheadSuggestions({
    term: draft,
    buildUrl,
    skip: draft === value,
    label: 'purchase order',
  });

  const handleChange = useCallback((text) => {
    setDraft(text);
    if (text === '' && value !== '') onCommit('');
  }, [value, onCommit]);

  const commitDraft = useCallback((text) => onCommit(text.trim()), [onCommit]);
  const pick = useCallback((po) => onCommit(po.po_number), [onCommit]);

  const clear = useCallback(() => {
    setDraft('');
    onCommit('');
  }, [onCommit]);

  // A number alone rarely tells two purchase orders apart — the supplier and
  // the expected date are what the merchant recognises the order by.
  const renderOption = useCallback((po) => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', minWidth: 0 }}>
      <span>{po.po_number}</span>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {[po.supplier_name, po.need_by_date && formatDate(po.need_by_date)]
          .filter(Boolean).join(' · ')}
      </Typography>
    </Box>
  ), [formatDate]);

  return (
    <TypeaheadSearchField
      // The bar owns its slot metrics (width, surface, mobile collapse);
      // this box wears the same class rather than restating them.
      className="filter-row-slot"
      label={t('purchaseOrders.filters.poNumberLabel')}
      placeholder={t('purchaseOrders.filters.poNumberPlaceholder')}
      value={draft}
      committedText={value}
      onChange={handleChange}
      suggestions={suggestions}
      loading={loading}
      failed={failed}
      getOptionKey={(po) => po.purchase_order_header_id}
      renderOption={renderOption}
      onPick={pick}
      onCommitText={commitDraft}
      // The draft follows the committed term, so an empty box can never have
      // a term behind it — one condition, not the item box's two.
      showClear={draft !== ''}
      onClear={clear}
      clearLabel={t('purchaseOrders.filters.clearPoNumber')}
      emptyText={t('purchaseOrders.filters.poNumberNoneFound')}
      footerText={t('purchaseOrders.filters.poNumberSearchHelper')}
    />
  );
}

function PurchaseOrders() {
  const { t } = useTranslation();
  const openPurchaseOrder = useOpenPurchaseOrder();
  const closePurchaseOrderTab = useClosePurchaseOrderTab();
  // PO_FINAL = ERP. ERP ownership is PER PO, not per store: a PO is
  // ERP-managed iff it carries an exact_po_id (a mirror Exact drives) — the
  // row shows its Exact PO ref, and poActions.js decides what the row still
  // offers. ``erpManaged`` (store has an active ERP) only shows the info banner.
  const { connector: erpConnector, error: connectorsError } = useActiveErp();
  const erpManaged = erpConnector !== null;
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatCurrency } = useFormatters();
  const gridState = useGridState('purchase_orders_master');

  // Default to 'active' (open + partially_received): the orders still owed,
  // sorted soonest-need-first by the per-status default ordering below.
  const [statusFilter, setStatusFilter] = useTabPersistedState(
    'statusFilter',
    'active',
  );
  // Filtered-dataset monetary totals — one aggregate per money column,
  // each with the currency that labels it (po_viewset._compute_totals).
  const [totals, setTotals] = useState(null);
  // {status: count} under every filter but status (po_viewset._status_counts)
  // — the numbers on the status buttons. null until the first list response.
  const [statusCounts, setStatusCounts] = useState(null);
  const [selectedLocation, setSelectedLocation] = useTabPersistedState('selectedLocation', "all");
  const [selectedProductCategory, setSelectedProductCategory] = useTabPersistedState('selectedProductCategory', null);
  const [selectedSupplier, setSelectedSupplier] = useTabPersistedState('selectedSupplier', null);
  // Committed PO-number term (see PoNumberFilter). Tab-persisted like every
  // other filter slot so an F5 restores what the merchant is looking at.
  const [poNumberFilter, setPoNumberFilter] = useTabPersistedState('poNumberFilter', '');
  const itemFilter = useItemFilter();

  const gridApiRef = useRef(null);
  const expandAll = useExpandAllRows();

  // Filled by useSsrmGrid below. The action handlers above it refresh the

  // grid through this, so every refetch also re-measures the columns.

  const refreshGridRef = useRef(null);
  const pageSize = 100;
  const PO_ENDPOINT = '/api/po-calculations/purchase-orders/';

  // The inline Expected-arrival edit, and the row actions — the same
  // lifecycle the PO details page offers. Receive opens the PO with every
  // open line filled in, ready to submit, because a receipt is booked line
  // by line on that page.
  const rescheduleMutation = useReschedulePO();
  const storeToday = useStoreToday();
  const mayCreateOrders = useWriteGate('po_final').mayWrite;
  const onReceive = useCallback(
    (po) => openPurchaseOrder(po.purchase_order_header_id, { receive: true }),
    [openPurchaseOrder],
  );
  // A deleted draft's tab, if open, would only say the PO is gone.
  const onDeleted = useCallback(
    (po) => closePurchaseOrderTab(po.purchase_order_header_id),
    [closePurchaseOrderTab],
  );
  const lifecycle = usePurchaseOrderLifecycle({ onReceive, onDeleted });

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    {
      field: "po_number",
      headerName: t('purchaseOrders.columns.poNumber'),
      ...flexColumn(),
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        // Mirror POs (managed in Exact) carry an "Exact <number>" chip next to
        // the Manexys PO number so the merchant can cross-reference the ERP.
        innerRenderer: (p) => {
          // The totals row's label: a total is no PO to link to.
          if (isPinnedTotalRow(p)) return p.value;
          // The number is the record link to the PO's details page.
          const link = (
            <TabLink to={`/purchase-orders/${p.data.purchase_order_header_id}`} onClick={(e) => e.stopPropagation()}>
              {p.value}
            </TabLink>
          );
          if (!p.data.is_erp_managed) return link;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {link}
              <Chip
                size="small"
                variant="outlined"
                color="info"
                sx={{ height: 18, fontSize: 11 }}
                label={p.data.exact_po_number || t('purchaseOrders.erpManaged.exactBadge')}
              />
            </span>
          );
        },
      },
    },
    {
      field: "supplier_name",
      headerName: t('purchaseOrders.columns.supplier'),
      // The text columns share the width the measured ones leave over; the
      // supplier name gets the larger share.
      ...flexColumn(2),
      valueFormatter: (params) => params.value || t('purchaseOrders.noSupplier'),
    },
    {
      field: "location_name",
      headerName: t('purchaseOrders.columns.location'),
      ...flexColumn(),
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: "status",
      headerName: t('purchaseOrders.columns.status'),
      type: 'centeredColumn',
      cellRenderer: PoStatusCell,
    },
    {
      field: "created_date",
      headerName: t('purchaseOrders.columns.created'),
      type: 'dateTimeColumn',
    },
    {
      field: "need_by_date",
      headerName: t('purchaseOrders.columns.expectedDeliveryDate'),
      type: 'dateColumn',
      // The displayed date depends on the PO's lifecycle stage. For the
      // issued stages (open, partially_received) it's the committed Expected
      // Date. For terminal stages it's the immutable timestamp of the action
      // that put the PO in that state — received_at / closed_at /
      // cancelled_at. This keeps a single column instead of four sparse
      // ones. A draft has no date: it stores none (schema invariant
      // po_header_need_by_iff_issued) and the server stamps one, per line,
      // only when it is issued.
      valueGetter: (params) => {
        const data = params.data;
        const s = data.status;
        if (s === 'received') return data.received_at ?? null;
        if (s === 'closed') return data.closed_at ?? null;
        if (s === 'cancelled') return data.cancelled_at ?? null;
        return data.need_by_date ?? null;
      },
      editable: (params) => canReschedule(params.data),
      // Past days greyed out, as on the PO page; the server refuses them too.
      cellEditorParams: { minDate: storeToday },
      cellClassRules: {
        // due_status comes from the backend (store-TZ aware) — see
        // core.date_utils.classify_due_status. The serializer already
        // gates it to active POs, so no status check is needed here.
        'ag-cell-attention-warning': (params) => params.data.due_status === 'today',
        'ag-cell-attention-error': (params) => params.data.due_status === 'overdue',
      },
    },
    {
      field: "total_supplier_currency",
      headerName: t('purchaseOrders.columns.total'),
      type: 'decimalColumn',
      valueFormatter: (params) => {
        // Every row carries its currency: PO rows because
        // PurchaseOrderHeader.CurrencyCode is non-null (PROTECT), the pinned
        // totals row because the backend emits the sum and the `currency`
        // labelling it together or not at all — a filtered set
        // spanning two supplier currencies has no total here, and
        // summaryAwareFormatter blanks the cell (po_viewset._compute_totals).
        // A draft whose prices are hidden (`shows_prices`, the server's
        // rule) shows no total either; the totals row carries no such flag
        // and keeps summing every row the filter holds.
        if (params.data.shows_prices === false) return '';
        return formatCurrency(params.value, params.data.currency.currency_code);
      },
    },
    {
      field: "total_store_currency",
      headerName: t('purchaseOrders.columns.totalStoreCcy'),
      type: 'decimalColumn',
      hide: true,
      // Format with the row's own reporting currency. The frozen total was
      // computed against store.currency at PO creation — for stores that
      // later changed Store.currency, that frozen value is denominated in
      // the *old* ccy, not today's. reporting_currency_code is the row's
      // basis snapshot; labeling against today's store.currency would be
      // a lie. See models.py PurchaseOrderHeader.ReportingCurrencyCode.
      valueFormatter: (params) => {
        // Every PO row carries a code: the column is NOT NULL. The pinned
        // totals row omits it when the filtered POs mix reporting currencies
        // (po_viewset._compute_totals), and that cell stays empty rather than
        // labelling a mixed sum with one currency.
        const code = params.data.reporting_currency_code;
        return code ? formatCurrency(params.value, code) : '';
      },
    },
    actionColumn({
      headerName: t('purchaseOrders.columns.actions'),
      // The PO number opens the PO; the cell holds what to do with it.
      cellRenderer: (params) => (isPinnedTotalRow(params) ? null : <PurchaseOrderRowActions po={params.data} />),
    })
  ]), [t, formatCurrency, storeToday]);

  // Pinned-bottom totals row. The backend sends one aggregate per money
  // column over the filtered dataset, each with the currency that labels it
  // and neither when that column's currency isn't shared by every row — so the
  // payload is fed straight through. Label sits in the po_number column.
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, 'po_number', t('grid.totals.label')),
    [totals, t],
  );

  // Every filter but the status buttons' — the scope the buttons count in.
  const otherFilters = useMemo(() => ({
    ...itemFilter.params,
    po_number: poNumberFilter || null,
    product_category_id: selectedProductCategory?.id || null,
    supplier_id: selectedSupplier?.supplier_id ?? null,
    location_code: selectedLocation === 'all' ? null : selectedLocation,
  }), [itemFilter.params, poNumberFilter, selectedProductCategory, selectedSupplier, selectedLocation]);
  // Filter params for the SSRM hook — status per STATUS_FILTERS above.
  const ssrmParams = useMemo(
    () => ({ status: statusParam(statusFilter), ...otherFilters }),
    [statusFilter, otherFilters],
  );
  // Counts of the previous scope would sit over the reloading grid.
  useChangeEffect(otherFilters, () => setStatusCounts(null));
  // An unset filter is null here and absent from the item params.
  const filtersActive = Object.values(otherFilters).some((value) => value !== null);

  const exportProps = {
    endpoint: '/api/po-calculations/purchase-orders/export/',
    filename: 'purchase_orders',
    // 'filtered' exports the rows the grid shows; 'all' every PO of the store.
    getParams: (scope) => (scope === 'all' ? '' : buildExportQuery(ssrmParams)),
  };

  const statusOptions = useMemo(() => Object.entries(STATUS_FILTERS).map(([value, statuses]) => ({
    value,
    label: t(`purchaseOrders.filter_${value}`),
    count: statusCounts
      ? statuses.reduce((sum, status) => sum + statusCounts[status], 0)
      : undefined,
  })), [statusCounts, t]);

  // Custom URL builder injects the per-status default ordering when the user
  // hasn't picked a sort. Backend has no notion of "default per status", so
  // this responsibility lives on the frontend.
  const DEFAULT_ORDERING_BY_STATUS = useMemo(() => ({
    active: 'need_by_date',
    draft: '-created_date',
    // Completed → backend exposes `terminal_date` = Coalesce(received_at,
    // closed_at, cancelled_at) so this sorts "most recently terminal first".
    completed: '-terminal_date',
    all: '-created_date',
  }), []);
  // `epochParams` is forwarded, not dropped: it carries the row-count request
  // for endpoints that count only when asked (core.pagination.
  // OptionalCountPagination). Swallowing it here would leave the grid sizing
  // its scrollbar from one block's row count, silently.
  const buildPoUrl = useCallback((endpoint, params, gridRequest, ps, epochParams) => {
    const base = buildSsrmUrl(endpoint, params, gridRequest, ps, epochParams);
    const hasSort = gridRequest?.sortModel && gridRequest.sortModel.length > 0;
    if (hasSort) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}ordering=${encodeURIComponent(DEFAULT_ORDERING_BY_STATUS[statusFilter])}`;
  }, [DEFAULT_ORDERING_BY_STATUS, statusFilter]);

  // po_viewset.list sends both on every block.
  const handleSsrmData = useCallback((data) => {
    setTotals(data.totals);
    setStatusCounts(data.status_counts);
  }, []);

  const handleSsrmError = useCallback((err) => {
    showToast(translateRequestError(err, t, 'purchaseOrders.messages.loadFailed'), 'error');
  }, [t]);

  // Stable identity — see ForecastGrid's getRowId for the AG Grid 35.3 rationale.
  const getRowId = useCallback((params) => String(params.data.purchase_order_header_id), []);

  const { onGridReady, onGridPreDestroyed, pagination } = useSsrmGrid({
    endpoint: PO_ENDPOINT,
    params: ssrmParams,
    pageSize,
    buildUrl: buildPoUrl,
    onData: handleSsrmData,
    onError: handleSsrmError,
    gridRef: gridApiRef,
    refreshRef: refreshGridRef,
  });

  const onCellValueChanged = useCallback(async (event) => {
    if (event.colDef.field !== 'need_by_date') return;
    const { data, newValue, oldValue } = event;
    if (newValue === oldValue) return;

    try {
      // A cleared cell is sent as is: the server refuses it (an issued PO
      // always carries a date) and the catch below puts the old date back.
      await rescheduleMutation.mutateAsync({
        id: data.purchase_order_header_id,
        needByDate: newValue,
      });
      showToast(t('purchaseOrders.messages.deliveryDateUpdated'), 'success');
    } catch (error) {
      // Revert the cell value on failure — TanStack doesn't know about
      // the SSRM grid's row state, so the rollback is local.
      event.node.setDataValue('need_by_date', oldValue);
      console.error('reschedulePO:', error.message);
      showToast(translateRequestError(error, t, 'purchaseOrders.messages.deliveryDateFailed'), 'error');
    }
  }, [t, rescheduleMutation]);

  // Clear user sort when status filter changes — the per-status default
  // ordering in buildPoUrl only applies when sortModel is empty. Without
  // this, switching from "active" (NeedByDate) to "received" (-modified_at)
  // would inherit the previous user click. The hook owns the actual refresh.
  //
  // MUST be useChangeEffect, not a plain useEffect([statusFilter]): a
  // dependency-array effect also fires on mount, clobbering the sort
  // useGridState just restored from ui_preferences — the grid then refetches
  // with an empty sortModel and falls back to the per-status default ordering
  // (-created_date on draft/all). useChangeEffect fires only on a genuine
  // statusFilter change.
  useChangeEffect(statusFilter, () => {
    gridApiRef.current?.applyColumnState({ defaultState: { sort: null } });
  });

  const noRowsMessage = t(`purchaseOrders.noRows_${statusFilter}`);

  return (
    <div className="page-container">
      <PageBanner severity="error" messages={[connectorsError]} />
      <PageBanner severity="info" messages={[erpManaged && t('purchaseOrders.erpManaged.banner')]} />
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.managePurchaseOrders')}
          subtitle={t('purchaseOrders.subtitle')}
          subtitleAutoHide
          toolbar={
            <FilterChips
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          }
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            <ItemFilterBar
              filter={itemFilter}
              leading={(
                <PoNumberFilter
                  value={poNumberFilter}
                  onCommit={setPoNumberFilter}
                  scope={ssrmParams}
                />
              )}
              productCategoryFilter={{ selected: selectedProductCategory, set: setSelectedProductCategory, show: true }}
              supplierFilter={{ selected: selectedSupplier, set: setSelectedSupplier }}
              locationFilter={{ selected: selectedLocation, set: setSelectedLocation }}
            />
            <ExpandAllButton {...expandAll} />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  // Per-status message ("No active purchase orders", etc.) is
                  // shown when statusFilter alone narrowed to zero rows.
                  title={noRowsMessage}
                  // Any other filter triggers the generic "No matches" copy.
                  filtersActive={filtersActive}
                >
                  <PurchaseOrderRowActionsScope
                    mayCreateOrders={mayCreateOrders}
                    onAction={lifecycle.run}
                    running={lifecycle.running}
                  >
                    <AgGridReact
                      {...defaultGridProps}
                      {...editableGridProps}
                      theme={gridTheme}
                      columnDefs={columnDefs}
                      getRowId={getRowId}
                      defaultColDef={defaultColDef}
                      columnTypes={columnTypes}
                      onGridReady={onGridReady}
                      onGridPreDestroyed={onGridPreDestroyed}
                      {...gridState}
                      rowModelType="serverSide"
                      serverSideSortAllLevels={true}
                      {...pagination}
                      masterDetail={true}
                      detailCellRenderer={PODetailRenderer}
                      detailRowAutoHeight={true}
                      isRowMaster={everyRowIsMaster}
                      {...expandAll.gridProps}
                      pinnedBottomRowData={pinnedBottomRowData}
                      getContextMenuItems={copyOnlyContextMenuItems}
                      onCellValueChanged={onCellValueChanged}
                    />
                  </PurchaseOrderRowActionsScope>
                </GridEmptyState>
              )}
            </GridViewport>

          </GridPage>
        </Tile>
      </div>

      {lifecycle.dialogs}
    </div>
  );
}

export default PurchaseOrders;
